import { ChevronDown, ChevronRight, Copy, Edit, MoreVertical, Plus, Save, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { HoverTip } from "src/components/ui/hover-tip";
import { IconButton } from "src/components/ui/icon-button";
import IncidentIoIcon from "src/components/ui/icons/incidentio";
import { Input } from "src/components/ui/input";
import { REDACTED as REDACTED_API_KEY } from "src/components/ui/secret-input";
import { ErrorAlert } from "src/components/ui/status-alert";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import type { WebhookCreatedData } from "src/components/WebhookConfigForm";
import { getProductName } from "src/utils/brandTheme";

import type { IncidentIoIntegration_integration$data } from "./__generated__/IncidentIoIntegration_integration.graphql";
import type { IncidentIoIntegrationCreateOrUpdateWebhookSubscriptionMutation } from "./__generated__/IncidentIoIntegrationCreateOrUpdateWebhookSubscriptionMutation.graphql";
import type { IncidentIoIntegrationDeleteIntegrationMutation } from "./__generated__/IncidentIoIntegrationDeleteIntegrationMutation.graphql";
import type { IncidentIoIntegrationDeleteWebhookSubscriptionMutation } from "./__generated__/IncidentIoIntegrationDeleteWebhookSubscriptionMutation.graphql";
import type { IncidentIoIntegrationQuery } from "./__generated__/IncidentIoIntegrationQuery.graphql";
import type { IncidentIoIntegrationUpdateIntegrationMutation } from "./__generated__/IncidentIoIntegrationUpdateIntegrationMutation.graphql";

interface IncidentIoIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  onIntegrationChange?: () => void;
  onWebhookCreated?: (webhook: WebhookCreatedData) => void;
  brandTheme?: string | null;
}

interface IncidentIoWebhookConfig {
  id?: string;
  webhook_subscription_name: string;
  webhook_secret: string;
  remote_id?: string;
  isEditing?: boolean;
  connectedAlerts?: ReadonlyArray<{ readonly id: any; readonly name: string | null | undefined }>;
}

const IncidentIoInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your incident.io dashboard.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">API Keys</span>.
          </li>
          <li>
            Click <span className="text-amber-600">+ Add new</span>.
          </li>
          <li>
            Enter a name for your key (e.g., &quot;{productName} Integration&quot;).
          </li>
          <li>
            Select the <span className="text-amber-600">View data</span> and <span className="text-amber-600">View all incident data</span> permissions.
          </li>
          <li>
            Click <span className="text-amber-600">Create</span> and copy the generated key.
          </li>
          <li>
            Paste the key into the API Key field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Webhook Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            First, click <span className="text-amber-600">Add Webhook Subscription</span> below to generate a webhook URL.
          </li>
          <li>
            Log into your incident.io dashboard.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">Webhooks</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Add endpoint</span>.
          </li>
          <li>
            Configure the webhook with:
            <ul className="list-disc list-inside ml-4 mt-1">
              <li>
                <strong>URL:</strong> Copy the webhook URL from the table below and paste it here
              </li>
              <li>
                <strong>Event subscriptions:</strong> Subscribe to the following events:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li><span className="text-amber-600">public_incident</span> — receives incident lifecycle updates (created, status changes, resolved)</li>
                  <li><span className="text-amber-600">private_incident</span> — same as above for private incidents</li>
                  <li><span className="text-amber-600">public_alert.alert_created_v1</span> — triggers alert agent investigation when a public alert fires</li>
                  <li><span className="text-amber-600">private_alert.alert_created_v1</span> — triggers alert agent investigation when a private alert fires</li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            Click <span className="text-amber-600">Create</span>.
          </li>
          <li>
            After creating the endpoint, copy the <span className="text-amber-600">Signing secret</span> from incident.io (it starts with <code>whsec_</code>).
          </li>
          <li>
            Paste the signing secret into the <span className="text-amber-600">Webhook Secret</span> field below and click <span className="text-amber-600">Save</span>.
          </li>
        </ol>
      </div>
    </div>
  );
};

const incidentIoIntegrationFragment = graphql`
  fragment IncidentIoIntegration_integration on GraphQLIntegration {
    id
    name
    integrationType
    credentials
  }
`;

const incidentIoIntegrationQuery = graphql`
  query IncidentIoIntegrationQuery {
    me {
      ...appMe
      organization {
        integrationWebhookPaths {
          INCIDENT_IO
        }
        webhookSubscriptions(subscriptionType: "INCIDENT_IO") {
          edges {
            node {
              id
              name
              remoteId
              secret
              connectedAlerts {
                id
                name
              }
            }
          }
        }
        integrations(integrationType: INCIDENT_IO) {
          edges {
            node {
              ...IncidentIoIntegration_integration
            }
          }
        }
      }
    }
  }
`;

const createOrUpdateWebhookSubscriptionMutation = graphql`
  mutation IncidentIoIntegrationCreateOrUpdateWebhookSubscriptionMutation(
    $input: CreateOrUpdateWebhookSubscriptionInput!
  ) {
    createOrUpdateWebhookSubscription(input: $input) {
      webhookSubscriptions {
        id
        name
        remoteId
      }
      error
    }
  }
`;

const deleteWebhookSubscriptionMutation = graphql`
  mutation IncidentIoIntegrationDeleteWebhookSubscriptionMutation(
    $input: DeleteWebhookSubscriptionInput!
  ) {
    deleteWebhookSubscription(input: $input) {
      success
      error
    }
  }
`;

const updateIntegrationMutation = graphql`
  mutation IncidentIoIntegrationUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
      }
      error
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation IncidentIoIntegrationDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

export default function IncidentIoIntegration({ isOpen, onClose, onRefetch, onIntegrationChange, onWebhookCreated, brandTheme }: IncidentIoIntegrationProps) {
  const productName = getProductName(brandTheme);
  const hasUnsavedChanges = () => {
    return webhookConfigs.some(config => config.isEditing) || isEditingApiConfig;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setUnsavedChangesDialog({ isOpen: true });
      return;
    }
    onClose();
  };

  const handleUnsavedChangesConfirm = () => {
    setUnsavedChangesDialog({ isOpen: false });
    onClose();
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ isOpen: false });
  };

  const [webhookConfigs, setWebhookConfigs] = useState<IncidentIoWebhookConfig[]>([]);
  const [apiKey, setApiKey] = useState<string>("");
  const [copiedPopup, setCopiedPopup] = useState<{ show: boolean; x: number; y: number }>({
    show: false,
    x: 0,
    y: 0,
  });
  const [showInstructions, setShowInstructions] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [savingApiKey, setSavingApiKey] = useState<boolean>(false);
  const [isEditingApiConfig, setIsEditingApiConfig] = useState<boolean>(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    config: IncidentIoWebhookConfig | null;
    index: number | null;
  }>({
    isOpen: false,
    config: null,
    index: null,
  });
  const [disconnectConfirmDialog, setDisconnectConfirmDialog] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  const [commitMutation, isMutationInFlight] = useMutation<IncidentIoIntegrationCreateOrUpdateWebhookSubscriptionMutation>(
    createOrUpdateWebhookSubscriptionMutation
  );

  const [commitDeleteMutation] = useMutation<IncidentIoIntegrationDeleteWebhookSubscriptionMutation>(
    deleteWebhookSubscriptionMutation
  );

  const [commitUpdateIntegrationMutation] = useMutation<IncidentIoIntegrationUpdateIntegrationMutation>(
    updateIntegrationMutation
  );

  const [commitDeleteIntegrationMutation] = useMutation<IncidentIoIntegrationDeleteIntegrationMutation>(
    deleteIntegrationMutation
  );

  const data = useLazyLoadQuery<IncidentIoIntegrationQuery>(
    incidentIoIntegrationQuery,
    {},
    { fetchPolicy: isOpen ? "network-only" : "store-only" },
  );

  const existingIntegrations = data?.me.organization?.integrations?.edges || [];
  const incidentIoIntegrationNode = existingIntegrations[0]?.node ?? null;
  const incidentIoIntegration = useFragment(
    incidentIoIntegrationFragment,
    incidentIoIntegrationNode,
  ) as IncidentIoIntegration_integration$data | null;

  useEffect(() => {
    if (isOpen && data && !hasLoadedData) {
      const existingSubscriptions = data.me.organization?.webhookSubscriptions?.edges || [];
      const loadedConfigs = existingSubscriptions.map(edge => ({
        id: edge.node.id,
        webhook_subscription_name: edge.node.name,
        webhook_secret: edge.node.secret || crypto.randomUUID(),
        remote_id: edge.node.remoteId || generateWebhookRemoteId(),
        isEditing: false,
        connectedAlerts: edge.node.connectedAlerts,
      }));
      setWebhookConfigs(loadedConfigs);

      if (incidentIoIntegration?.credentials) {
        try {
          const credentials = incidentIoIntegration.credentials;
          if (credentials.api_key && credentials.api_key !== REDACTED_API_KEY) {
            setApiKey(credentials.api_key);
          } else if (credentials.api_key === REDACTED_API_KEY) {
            setApiKey(REDACTED_API_KEY);
          }
        } catch (error) {
          console.error("Error parsing integration credentials:", error);
        }
      }

      setHasLoadedData(true);
    } else if (!isOpen) {
      setHasLoadedData(false);
      setFormError(null);
      setApiKey("");
      setIsEditingApiConfig(false);
    }
  }, [isOpen, data, hasLoadedData, incidentIoIntegration]);

  const generateWebhookRemoteId = () => {
    return crypto.randomUUID();
  };

  const getWebhookDisplayUrl = (remoteId: string) => {
    const baseUrl = data?.me.organization?.integrationWebhookPaths?.INCIDENT_IO || "";
    return baseUrl ? `${baseUrl}/${remoteId}` : "";
  };

  const addWebhookConfig = () => {
    const remoteId = generateWebhookRemoteId();

    setWebhookConfigs([
      ...webhookConfigs,
      {
        webhook_subscription_name: "",
        webhook_secret: "",
        remote_id: remoteId,
        isEditing: true,
      },
    ]);
  };

  const removeWebhookConfig = async (config: IncidentIoWebhookConfig, index: number) => {
    if (config.id) {
      setFormError(null);

      commitDeleteMutation({
        variables: {
          input: { id: config.id }
        },
        onCompleted: (response, errors) => {
          if (errors && errors.length > 0) {
            setFormError(errors.map(e => e.message).join(", "));
            return;
          }

          if (response.deleteWebhookSubscription?.error) {
            setFormError(response.deleteWebhookSubscription.error);
            return;
          }

          if (response.deleteWebhookSubscription?.success) {
            setWebhookConfigs((prev) => prev.filter((_, i) => i !== index));
            setFormError(null);
          }
        },
        onError: error => {
          setFormError(error.message);
        }
      });
    } else {
      setWebhookConfigs((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleDeleteClick = (config: IncidentIoWebhookConfig, index: number) => {
    setDeleteConfirmDialog({
      isOpen: true,
      config,
      index,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmDialog.config && deleteConfirmDialog.index !== null) {
      removeWebhookConfig(deleteConfirmDialog.config, deleteConfirmDialog.index);
    }
    setDeleteConfirmDialog({
      isOpen: false,
      config: null,
      index: null,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({
      isOpen: false,
      config: null,
      index: null,
    });
  };

  const saveWebhookConfig = async (config: IncidentIoWebhookConfig, index: number) => {
    if (!config.webhook_subscription_name || !config.webhook_secret || !config.remote_id) {
      setFormError("Please fill in all required fields.");
      return;
    }

    setFormError(null);
    setSavingIndex(index);

    const input = {
      webhookSubscriptionType: "INCIDENT_IO" as const,
      webhookConfigs: [{
        id: config.id || undefined,
        name: config.webhook_subscription_name,
        remoteId: config.remote_id,
        secret: config.webhook_secret,
      }],
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        setSavingIndex(null);

        if (errors && errors.length > 0) {
          setFormError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.createOrUpdateWebhookSubscription?.error) {
          setFormError(response.createOrUpdateWebhookSubscription.error);
          return;
        }

        const savedConfig = response.createOrUpdateWebhookSubscription?.webhookSubscriptions?.[0];
        if (savedConfig) {
          const isNew = !config.id;
          setWebhookConfigs(prev => {
            const next = [...prev];
            next[index] = {
              ...config,
              id: savedConfig.id,
              isEditing: false,
            };
            return next;
          });
          setFormError(null);
          onIntegrationChange?.();
          if (isNew) {
            onWebhookCreated?.({
              id: savedConfig.id,
              name: savedConfig.name,
              remoteId: savedConfig.remoteId,
              webhookSubscriptionType: "INCIDENT_IO",
            });
          }
        }
      },
      onError: error => {
        setSavingIndex(null);
        setFormError(error.message);
      }
    });
  };

  const saveApiKey = async () => {
    if (!apiKey) {
      setFormError("Please enter an API key.");
      return;
    }

    setSavingApiKey(true);
    setFormError(null);

    const credentials = JSON.stringify({
      api_key: apiKey,
    });

    commitUpdateIntegrationMutation({
      variables: {
        input: {
          integrationType: "INCIDENT_IO",
          credentials: credentials,
        },
      },
      onCompleted: (response, errors) => {
        setSavingApiKey(false);

        if (errors && errors.length > 0) {
          setFormError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.updateIntegration?.error) {
          setFormError(response.updateIntegration.error);
          return;
        }

        setFormError(null);
        setIsEditingApiConfig(false);
        setApiKey(REDACTED_API_KEY);
        onIntegrationChange?.();
      },
      onError: error => {
        setSavingApiKey(false);
        setFormError(error.message);
      }
    });
  };

  const editApiConfig = () => {
    if (apiKey === REDACTED_API_KEY) {
      setApiKey("");
    }
    setIsEditingApiConfig(true);
  };

  const editWebhookConfig = (index: number) => {
    const updated = [...webhookConfigs];
    const current = updated[index];
    updated[index] = {
      ...current,
      isEditing: true,
      webhook_secret:
        current.webhook_secret === REDACTED_API_KEY ? "" : current.webhook_secret,
    };
    setWebhookConfigs(updated);
  };

  const updateWebhookConfig = (index: number, field: keyof IncidentIoWebhookConfig, value: string) => {
    const updated = [...webhookConfigs];
    updated[index] = { ...updated[index], [field]: value };
    setWebhookConfigs(updated);
  };

  const copyToClipboard = async (text: string, event: React.MouseEvent) => {
    try {
      await navigator.clipboard.writeText(text);

      setCopiedPopup({
        show: true,
        x: event.clientX,
        y: event.clientY,
      });

      setTimeout(() => {
        setCopiedPopup({ show: false, x: 0, y: 0 });
      }, 800);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDisconnectClick = () => {
    setDisconnectConfirmDialog({ isOpen: true });
    setDisconnectError(null);
  };

  const handleDisconnectConfirm = () => {
    if (!incidentIoIntegration?.id) {
      setDisconnectError("Integration not found");
      return;
    }

    setIsDisconnecting(true);
    setDisconnectError(null);

    commitDeleteIntegrationMutation({
      variables: {
        input: {
          id: incidentIoIntegration.id,
        },
      },
      onCompleted: (response, errors) => {
        setIsDisconnecting(false);

        if (errors && errors.length > 0) {
          setDisconnectError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setDisconnectError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setDisconnectConfirmDialog({ isOpen: false });
          onClose();
          onRefetch?.();
        }
      },
      onError: error => {
        setIsDisconnecting(false);
        setDisconnectError(error.message);
      }
    });
  };

  const handleDisconnectCancel = () => {
    setDisconnectConfirmDialog({ isOpen: false });
    setDisconnectError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <IncidentIoIcon className="h-6 w-6" />
            Connect incident.io
          </DialogTitle>
          <DialogDescription>
            Connect your incident.io account for {productName} to receive incident events. You can add multiple webhook subscriptions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <IncidentIoInstructions productName={productName} />
            </div>
          </div>

          {/* API Configuration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">API Configuration</h3>

            <div className="grid grid-cols-[1fr_auto] gap-4 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                API Key
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
              <Input
                placeholder="Enter incident.io API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="bg-card border-border text-foreground"
                disabled={!isEditingApiConfig}
              />

              <div className="flex gap-2 items-center">
                {isEditingApiConfig ? (
                  <IconButton
                    icon={Save}
                    variant="blue"
                    size="sm"
                    onClick={saveApiKey}
                    disabled={savingApiKey}
                    className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-blue-700 hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                    title={savingApiKey ? "Saving..." : "Save API configuration"}
                  />
                ) : (
                  <IconButton
                    icon={Edit}
                    variant="none"
                    size="sm"
                    onClick={editApiConfig}
                    className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                    title="Edit API configuration"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Webhook Subscriptions Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Webhook Subscriptions</h3>

            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                Webhook Subscription Name
                <HoverTip content={`This is the display name that will only be used here on the ${productName} app to help with differentiation.`} />
              </div>
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                Webhook Secret
                <HoverTip content="The signing secret from incident.io (starts with whsec_). Create the webhook endpoint in incident.io first, then copy the signing secret and paste it here. It will be redacted after you close this dialog." />
              </div>
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                Webhook URL
                <HoverTip content="Auto-generated webhook URL to use in incident.io. This URL is unique for each webhook subscription." />
              </div>
              <div></div>
            </div>

            {webhookConfigs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No webhook subscriptions yet.</p>
                <p className="text-sm">Click "Add Webhook Subscription" to get started.</p>
              </div>
            ) : (
              webhookConfigs.map((config, index): React.ReactNode => (
                <div key={config.id || index} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 items-center">
                  <Input
                    placeholder="Enter subscription name"
                    value={config.webhook_subscription_name}
                    onChange={(e) =>
                      updateWebhookConfig(
                        index,
                        "webhook_subscription_name",
                        e.target.value,
                      )
                    }
                    className="bg-card border-border text-foreground"
                    disabled={!config.isEditing && !!config.id}
                  />

                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Paste signing secret from incident.io (whsec_...)"
                      value={config.webhook_secret}
                      onChange={(e) =>
                        updateWebhookConfig(
                          index,
                          "webhook_secret",
                          e.target.value,
                        )
                      }
                      className="bg-card border-border text-foreground font-mono text-xs"
                      disabled={!config.isEditing && !!config.id}
                    />
                    {!config.isEditing && config.webhook_secret && config.webhook_secret !== REDACTED_API_KEY && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0"
                        onClick={(e) =>
                          copyToClipboard(config.webhook_secret, e)
                        }
                        title="Copy webhook secret"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Input
                      value={config.remote_id ? getWebhookDisplayUrl(config.remote_id) : ""}
                      className="bg-accent border-border text-muted-foreground font-mono text-xs"
                      disabled={true}
                      readOnly={true}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0"
                      onClick={(e) =>
                        config.remote_id && copyToClipboard(getWebhookDisplayUrl(config.remote_id), e)
                      }
                      title="Copy webhook URL"
                      disabled={!config.remote_id}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>

                  <div className="flex gap-2 items-center">
                    {config.isEditing ? (
                      <IconButton
                        icon={Save}
                        variant="blue"
                        size="sm"
                        onClick={() => saveWebhookConfig(config, index)}
                        disabled={savingIndex === index || isMutationInFlight}
                        className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-blue-700 hover:bg-blue-600 disabled:opacity-50"
                        title={savingIndex === index ? "Saving..." : "Save configuration"}
                      />
                    ) : config.id ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <IconButton
                            icon={MoreVertical}
                            variant="none"
                            size="sm"
                            className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                            title="More options"
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-card border-border text-foreground">
                          <DropdownMenuItem
                            onClick={() => editWebhookConfig(index)}
                            className="hover:bg-accent cursor-pointer"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(config, index)}
                            className="hover:bg-accent cursor-pointer text-red-400 hover:text-red-300"
                          >
                            <Trash className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </div>
              ))
            )}

            <div className="flex justify-start">
              <Button
                variant="outline"
                onClick={addWebhookConfig}
                className="border-border text-muted-foreground hover:text-foreground hover:bg-card/50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Webhook Subscription
              </Button>
            </div>
          </div>

          {formError && (
            <ErrorAlert icon="none">
              {formError}
            </ErrorAlert>
          )}

          {/* Disconnect section */}
          {incidentIoIntegration && (
            <div className="pt-6 border-t border-accent">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-muted-foreground">
                    Removing this integration will delete all associated webhook subscriptions.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDisconnectClick}
                >
                  Disconnect
                </Button>
              </div>
            </div>
          )}


        </div>
      </DialogContent>

      {/* Copy confirmation popup */}
      {copiedPopup.show && (
        <div
          className="fixed z-[1000] bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium shadow-lg pointer-events-none animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
          style={{
            left: copiedPopup.x - 25,
            top: copiedPopup.y - 40,
          }}
        >
          Copied!
        </div>
      )}

      {/* Delete confirmation dialog */}
      <UIConfirmationDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Removal"
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
      >
        <div className="space-y-3">
          <p>
            Are you sure you want to remove the webhook subscription "{deleteConfirmDialog.config?.webhook_subscription_name}"?
            This action cannot be undone.
          </p>

          {deleteConfirmDialog.config?.connectedAlerts && deleteConfirmDialog.config.connectedAlerts.length > 0 && (
            <div className="border border-amber-600 rounded-md p-3 bg-amber-900/20">
              <p className="font-medium text-amber-400 mb-2">
                This integration is connected to {deleteConfirmDialog.config.connectedAlerts.length} alert(s):
              </p>
              <ul className="space-y-1 text-sm">
                {deleteConfirmDialog.config.connectedAlerts.map((alert): React.ReactNode => (
                  <li key={alert.id} className="text-muted-foreground">
                    {alert.name || "Unnamed Alert"}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-amber-300 mt-2">
                These configurations will also be deleted and {productName} will no longer respond to the alerts.
              </p>
            </div>
          )}
        </div>
      </UIConfirmationDialog>

      {/* Disconnect confirmation dialog */}
      <Dialog open={disconnectConfirmDialog.isOpen} onOpenChange={() => setDisconnectConfirmDialog({ isOpen: false })}>
        <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Disconnect Integration</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to disconnect the incident.io integration? This will also remove all associated webhook subscriptions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {disconnectError && (
            <ErrorAlert icon="none">
              {disconnectError}
            </ErrorAlert>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={handleDisconnectCancel}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnectConfirm}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes confirmation dialog */}
      <Dialog open={unsavedChangesDialog.isOpen} onOpenChange={() => setUnsavedChangesDialog({ isOpen: false })}>
        <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You have unsaved changes. Are you sure you want to close without saving? All unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={handleUnsavedChangesCancel}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnsavedChangesConfirm}
            >
              Discard Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
